import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ScreenshotLightbox from './ScreenshotLightbox';
import './ScreenshotStrip.css';

/**
 * Portrait placeholder held until an image reports its intrinsic size. Tiles are
 * a fixed height and a measured width, so without a placeholder every tile would
 * be zero-wide and the strip would jump as the images arrive.
 */
const PLACEHOLDER_RATIO = '9 / 16';

/** Pointer travel, in px, past which a press counts as a drag and not a tap. */
const DRAG_SLOP = 4;

/**
 * The horizontal screenshot strip, plus the lightbox it opens.
 *
 * Tile width comes from each image's own aspect ratio rather than a fixed box,
 * so portrait phone captures and landscape tablet captures both sit correctly
 * against the shared 230px height. The ratios are keyed by source URL, so they
 * stay correct if the same screenshot appears twice and can never be read off by
 * a stale index.
 *
 * The lightbox index lives here: it belongs to the strip that opened it, not to
 * the modal's own open/closed state.
 */
export default function ScreenshotStrip({ screenshots, appName }) {
  const [ratios, setRatios] = useState({});
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const trackRef = useRef(null);
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0, moved: 0 });

  const shots = screenshots || [];

  function handleLoad(event) {
    const image = event.currentTarget;
    const { naturalWidth, naturalHeight } = image;
    if (!naturalWidth || !naturalHeight) return;
    // Keyed by the src as given, not `image.src`, which the DOM resolves to an
    // absolute URL that would never match the value the tiles look up by.
    setRatios((prev) => ({
      ...prev,
      [image.getAttribute('src')]: `${naturalWidth} / ${naturalHeight}`,
    }));
  }

  function handleMouseDown(event) {
    const track = trackRef.current;
    drag.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: track ? track.scrollLeft : 0,
      moved: 0,
    };
  }

  function handleMouseMove(event) {
    if (!drag.current.active) return;
    event.preventDefault();
    const delta = event.clientX - drag.current.startX;
    drag.current.moved = Math.max(drag.current.moved, Math.abs(delta));
    if (trackRef.current) {
      trackRef.current.scrollLeft = drag.current.scrollLeft - delta;
    }
  }

  function endDrag() {
    drag.current.active = false;
  }

  // A drag ends in a click on whichever tile was under the pointer; opening the
  // lightbox then would make the strip impossible to scroll by dragging.
  function openTile(index) {
    return () => {
      if (drag.current.moved > DRAG_SLOP) return;
      setLightboxIndex(index);
    };
  }

  if (shots.length === 0) return null;

  return (
    <section className="itcStrip">
      <h2 className="itcStripLabel">Screenshots</h2>

      <div
        className="itcStripTrack"
        data-testid="itcStripTrack"
        ref={trackRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        role="presentation"
      >
        {shots.map((src, index) => (
          <button
            type="button"
            key={`${src}-${index}`}
            className="itcStripTile"
            style={{ aspectRatio: ratios[src] || PLACEHOLDER_RATIO }}
            onClick={openTile(index)}
            aria-label={`Open screenshot ${index + 1} of ${shots.length}`}
          >
            <img src={src} alt="" onLoad={handleLoad} draggable={false} />
          </button>
        ))}
      </div>

      {lightboxIndex !== null && (
        <ScreenshotLightbox
          screenshots={shots}
          index={lightboxIndex}
          appName={appName}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </section>
  );
}

ScreenshotStrip.propTypes = {
  screenshots: PropTypes.arrayOf(PropTypes.string),
  appName: PropTypes.string.isRequired,
};
